import React, { useState } from 'react';
import { X, User, MapPin, AlertTriangle, Plus, Activity } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { BENEFICIARY_STATUS, STATUS_LABELS } from '../../constants/beneficiaryStatuses';

const AddClientModal = ({ db, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthDate: '',
    typeEntite: 'Particulier',
    address: {
      street: '',
      postalCode: '',
      city: '',
      country: ''
    },
    additionalInfo: '',
    allergies: '',
    treatments: '',
    autonomyLevel: '',
    emergencyContact: '',
    status: BENEFICIARY_STATUS.ACTIVE,
    color: '#4ECDC4' // Default color
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const clientData = {
        ...formData,
        // color is now in formData
        // Champs de compatibilité
        isActive: formData.status === BENEFICIARY_STATUS.ACTIVE || formData.status === BENEFICIARY_STATUS.PENDING,
        isArchived: formData.status === BENEFICIARY_STATUS.ARCHIVED,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'clients'), clientData);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du bénéficiaire:', error);
      alert('Erreur lors de l\'ajout du bénéficiaire');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Nouveau bénéficiaire</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Ajouter un nouveau bénéficiaire au système</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Corps du modal */}
        <div className="p-6">
          <form onSubmit={handleSubmit}>
            {/* Informations principales */}
            <div className="mb-6">
              <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                <User size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                Informations principales
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto shadow-inner transition-colors duration-300"
                    style={{ backgroundColor: formData.color || '#4ECDC4' }}
                  >
                    <span className="text-white font-bold text-2xl shadow-sm">
                      {formData.firstName ? formData.firstName.charAt(0).toUpperCase() : 'N'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mt-3 truncate">
                    {formData.firstName || 'Nouveau'} {formData.lastName || 'Bénéficiaire'}
                  </h3>

                  {/* Color Picker */}
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-3 uppercase tracking-wider">Couleur du dossier</label>
                    <div className="flex flex-wrap justify-center gap-3 max-w-[280px] mx-auto">
                      {[
                        // Rouges / Roses
                        '#FF6B6B', '#F44336', '#E91E63', '#F06292',
                        // Oranges / Jaunes
                        '#FFA07A', '#FF9800', '#FFD54F', '#FFC107',
                        // Verts / Turquoises
                        '#4ECDC4', '#009688', '#AED581', '#8BC34A',
                        // Bleus
                        '#45B7D1', '#03A9F4', '#3F51B5', '#7986CB',
                        // Violets / Autres
                        '#9575CD', '#9C27B0', '#607D8B', '#546E7A'
                      ].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, color }))}
                          className={`w-8 h-8 rounded-full transition-all duration-200 flex items-center justify-center ${formData.color === color ? 'ring-4 ring-offset-2 ring-blue-500/50 scale-110 shadow-lg' : 'hover:scale-110 hover:shadow-md'}`}
                          style={{ backgroundColor: color }}
                          title={color}
                        >
                          {formData.color === color && <span className="text-white text-xs drop-shadow-md">✓</span>}
                        </button>
                      ))}

                      {/* Custom color input fallback */}
                      <div className="relative w-8 h-8">
                        <input
                          type="color"
                          value={formData.color}
                          onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full h-full p-0 border-0 rounded-full overflow-hidden cursor-pointer opacity-0 absolute inset-0"
                        />
                        <div className="w-full h-full rounded-full border-2 border-dashed border-gray-400 flex items-center justify-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Autre couleur">
                          <span className="text-lg leading-none mb-0.5">+</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="text-xs font-medium bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-2 py-1 outline-none focus:border-blue-500"
                    >
                      {Object.values(BENEFICIARY_STATUS).map(status => (
                        <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="firstName">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        placeholder="Prénom du bénéficiaire"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="lastName">
                        Nom *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        placeholder="Nom du bénéficiaire"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="email">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="email@exemple.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="phoneNumber">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="+33 1 23 45 67 89"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="birthDate">
                        Date de naissance
                      </label>
                      <input
                        type="date"
                        id="birthDate"
                        name="birthDate"
                        value={formData.birthDate}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="typeEntite">
                        Type d'entité
                      </label>
                      <select
                        id="typeEntite"
                        name="typeEntite"
                        value={formData.typeEntite}
                        onChange={handleInputChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="Particulier">Particulier</option>
                        <option value="Entreprise">Entreprise</option>
                        <option value="Association">Association</option>
                        <option value="Collectivité">Collectivité</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Adresse complète */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                <MapPin size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                Adresse
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="street">
                    Rue
                  </label>
                  <input
                    type="text"
                    id="street"
                    name="street"
                    value={formData.address.street}
                    onChange={handleAddressChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Numéro et nom de rue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="postalCode">
                    Code postal
                  </label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.address.postalCode}
                    onChange={handleAddressChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="75000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="city">
                    Ville
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.address.city}
                    onChange={handleAddressChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Paris"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="country">
                    Pays
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.address.country}
                    onChange={handleAddressChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="France"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="additionalInfo">
                    Informations complémentaires
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Informations supplémentaires sur l'adresse..."
                  />
                </div>
              </div>
            </div>

            {/* Informations médicales */}
            <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                <AlertTriangle size={16} className="mr-2 text-orange-600 dark:text-orange-400" />
                Informations médicales
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="allergies">
                    Allergies
                  </label>
                  <textarea
                    id="allergies"
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="2"
                    placeholder="Allergies connues..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="treatments">
                    Traitements
                  </label>
                  <textarea
                    id="treatments"
                    name="treatments"
                    value={formData.treatments}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="2"
                    placeholder="Traitements en cours..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="autonomyLevel">
                    Niveau d'autonomie
                  </label>
                  <select
                    id="autonomyLevel"
                    name="autonomyLevel"
                    value={formData.autonomyLevel}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="Autonome">Autonome</option>
                    <option value="Semi-autonome">Semi-autonome</option>
                    <option value="Dépendant">Dépendant</option>
                    <option value="Très dépendant">Très dépendant</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="emergencyContact">
                    Personne à contacter
                  </label>
                  <input
                    type="text"
                    id="emergencyContact"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nom et téléphone d'urgence"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || !formData.firstName || !formData.lastName}
              >
                <Plus size={16} className="mr-2" />
                {isSubmitting ? 'Création...' : 'Créer le bénéficiaire'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddClientModal;
